import { useNavigate } from "react-router-dom";
import { ExternalLink, Link2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { labelOf } from "./resourceApi";
import { fmtDate } from "@/components/files/fileUtils";

function Field({ label, children }) {
  return (
    <div>
      <div className="text-2xs font-semibold uppercase tracking-[0.15em] text-zinc-500">{label}</div>
      <div className="mt-0.5 text-sm text-zinc-800">{children || "—"}</div>
    </div>
  );
}

function ModuleLinks({ links }) {
  const navigate = useNavigate();
  if (!links?.length) return null;
  return (
    <Card className="space-y-2 rounded-lg border border-zinc-200 p-4 shadow-none" data-testid="resource-module-links">
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500"><Link2 className="h-3.5 w-3.5" /> Linked modules</div>
      {links.map((l) => (
        <div key={`${l.kind}-${l.ref_id}`} className="text-sm">
          {l.kind === "checkin_template" ? (
            <button type="button" onClick={() => navigate("/progress-review?tab=templates")} data-testid={`resource-open-link-${l.kind}`}
                    className="inline-flex items-center gap-1 font-medium text-sky-700 hover:underline">
              Progress Review · {l.label} <ExternalLink className="h-3 w-3" />
            </button>
          ) : (
            <div data-testid={`resource-open-link-${l.kind}`}>
              <span className="font-medium">Outcome measure · {l.label}</span>
              <div className="text-xs text-zinc-500">Recorded and scored on each client&apos;s Health record → Outcome measures.</div>
            </div>
          )}
        </div>
      ))}
      <div className="text-2xs text-zinc-500">Responses, schedules and scores stay in their own module — the library only links to them.</div>
    </Card>
  );
}

export function ResourceMetaCard({ res, meta }) {
  return (
    <div className="space-y-4">
      <Card className="space-y-3 rounded-lg border border-zinc-200 p-4 shadow-none" data-testid="resource-meta-card">
        {res.description && <p className="whitespace-pre-wrap text-sm text-zinc-700">{res.description}</p>}
        <Field label="Type">{labelOf(meta.types, res.type)}</Field>
        <Field label="Categories">{res.categories.map((c) => labelOf(meta.categories, c)).join(", ")}</Field>
        <Field label="Tags">{res.tags.join(", ")}</Field>
        <Field label="Applicable programs">{res.programs.join(", ")}</Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Owner">{res.owner_name}</Field>
          <Field label="Clinical reviewer">{res.clinical_reviewer_name || "Any approver"}</Field>
          <Field label="Last review">{fmtDate(res.last_review_date)}</Field>
          <Field label="Next review">{fmtDate(res.next_review_date)}</Field>
        </div>
        {res.restrictions && <Field label="Restrictions">{res.restrictions}</Field>}
        {res.audience === "client" && <Field label="Reception">{res.reception_can_send ? "May send to clients" : "Not permitted to send"}</Field>}
        <Field label="Created">{res.created_by_name} · {fmtDate(res.created_at)}</Field>
      </Card>
      <ModuleLinks links={res.links} />
    </div>
  );
}
